import apiClient from '../api/api';

export interface DatabaseConfig {
  _id: string;
  config_name: string;
  database_provider: string;
  connection_string: string;
  database_name: string;
  collection_name: string;
  ingest_mode?: string;
  data_granularity?: string;
  data_format?: string;
  filename_field?: string;
  data_file_field?: string;
  is_deleted: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface CreateDatabaseConfigData {
  config_name: string;
  database_provider: string;
  connection_string: string;
  database_name: string;
  collection_name: string;
  ingest_mode?: string;
  data_granularity?: string;
  data_format?: string;
  filename_field?: string;
  data_file_field?: string;
}

export interface UpdateDatabaseConfigData extends CreateDatabaseConfigData { }

export interface DatabaseConfigListParams {
  skip: number;
  limit: number;
  config_name?: string;
}

export interface DatabaseConfigListResponse {
  success: boolean;
  code: number;
  data: {
    total_count: number;
    databaseConfigList: DatabaseConfig[];
    last_modified: string;
  };
  headers: Record<string, any>;
}

export interface DatabaseConfigResponse {
  success: boolean;
  code: number;
  data: DatabaseConfig;
  headers: Record<string, any>;
}

export interface DatabaseConfigStats {
  total_configs: number;
  active_configs: number;
  mongodb_configs: number;
}

export const DATABASE_PROVIDERS = [
  { value: 'mongodb', label: 'MongoDB' },
  { value: 'postgres', label: 'PostgreSQL' },
] as const;

// Live-introspection results used to populate the cascading dropdowns.
export interface DbInspectItem {
  name: string;
  size_bytes: number;
  count?: number;
  storage_bytes?: number;
  empty?: boolean;
}

// Warn threshold for "this dataset is large" — 1 GiB.
export const LARGE_DATASET_BYTES = 1024 * 1024 * 1024;

class DatabaseConfigService {
  // GET: Fetch list with pagination and filters
  async getDatabaseConfigs(params: DatabaseConfigListParams): Promise<DatabaseConfigListResponse> {
    try {
      const response = await apiClient.post('/dms-v2-srv/database-config-srv/utils/list', params);
      return response.data;
    } catch (error) {
      console.error('Error fetching database configs:', error);
      throw error;
    }
  }

  // GET: Fetch single database config by ID
  async getDatabaseConfigById(id: string): Promise<DatabaseConfig> {
    try {
      const response = await apiClient.get(`/dms-v2-srv/database-config-srv/${id}`);
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching database config ${id}:`, error);
      throw error;
    }
  }

  // POST: Create new database config
  async createDatabaseConfig(configData: CreateDatabaseConfigData): Promise<DatabaseConfig> {
    try {
      const response = await apiClient.post('/dms-v2-srv/database-config-srv', configData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating database config:', error);
      throw error;
    }
  }

  // PUT: Update existing database config
  async updateDatabaseConfig(id: string, configData: UpdateDatabaseConfigData): Promise<DatabaseConfig> {
    try {
      const response = await apiClient.put(`/dms-v2-srv/database-config-srv/${id}`, configData);
      return response.data.data;
    } catch (error) {
      console.error(`Error updating database config ${id}:`, error);
      throw error;
    }
  }

  // DELETE: Remove database config
  async deleteDatabaseConfig(id: string): Promise<void> {
    try {
      await apiClient.delete(`/dms-v2-srv/database-config-srv/${id}`);
    } catch (error) {
      console.error(`Error deleting database config ${id}:`, error);
      throw error;
    }
  }

  // GET: Fetch database config statistics
  async getDatabaseConfigStats(): Promise<DatabaseConfigStats> {
    try {
      // Calculate stats from the list since no dedicated stats endpoint exists
      const response = await this.getDatabaseConfigs({ skip: 0, limit: 1000 });
      const configs = response.data.databaseConfigList;

      const stats: DatabaseConfigStats = {
        total_configs: configs.length,
        active_configs: configs.filter(c => !c.is_deleted).length,
        mongodb_configs: configs.filter(c => c.database_provider.toLowerCase() === 'mongodb').length,
      };

      return stats;
    } catch (error) {
      console.error('Error fetching database config stats:', error);
      // Return default stats on error
      return {
        total_configs: 0,
        active_configs: 0,
        mongodb_configs: 0,
      };
    }
  }

  // Helper function to get available database providers
  getAvailableProviders() {
    return DATABASE_PROVIDERS;
  }

  // POST: Introspect a connection string and list its databases (with sizes).
  async inspectDatabases(provider: string, connectionString: string): Promise<DbInspectItem[]> {
    const response = await apiClient.post('/dms-v2-srv/database-config-srv/utils/inspect/databases', {
      database_provider: provider,
      connection_string: connectionString,
    });
    return response.data?.data?.databases || [];
  }

  // POST: List collections (MongoDB) / tables (PostgreSQL) in a database, with sizes + counts.
  async inspectCollections(provider: string, connectionString: string, databaseName?: string): Promise<DbInspectItem[]> {
    const response = await apiClient.post('/dms-v2-srv/database-config-srv/utils/inspect/collections', {
      database_provider: provider,
      connection_string: connectionString,
      database_name: databaseName,
    });
    return response.data?.data?.collections || [];
  }
}

export const databaseConfigService = new DatabaseConfigService();
export default databaseConfigService;